import { Location as AngularLocation } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MenuController, NavController, PopoverController, IonContent } from '@ionic/angular';
import { MapComponent } from 'app/components/map/map.component';
import { ProtagonistsListComponent } from 'app/components/protagonists-list/protagonists-list.component';
import { LocationListComponent } from 'app/components/location-list/location-list.component';
import { StorytellerOverlayComponent } from 'app/components/storyteller-overlay/storyteller-overlay.component';
import { LocationType } from 'app/constants';
import { Location, Protagonist, Route, Story } from 'app/interfaces/api/types';

import {
  ContextService,
  LocationService,
  ProtagonistService,
  RouteService,
  StoryService,
  SettingsService,
  HistoryService
} from 'app/services';

import { DomHelper } from 'app/classes/dom-helper/dom-helper';

import * as jQuery from 'jquery';
import { Subscription } from 'rxjs';
import { Environment } from 'environments/environment';


@Component({
  selector: 'app-story',
  templateUrl: './story.page.html',
  styleUrls: ['./story.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StoryPage implements OnInit {
  @ViewChild('content', { read: ElementRef, static: false })
  private content: ElementRef;

  @ViewChild('header', { read: ElementRef, static: false })
  private header: ElementRef;

  @ViewChild('tooltip', { read: ElementRef, static: false })
  private tooltip: ElementRef;

  public popover: any = undefined;
  public popoverId: string;
  public contextProtagonist: Protagonist;
  public contextRoute: Route;

  public story: Story;
  public nextStory: Story | null;
  public previousStory: Story | null;

  public protagonist: Protagonist;
  public defaultProtagonistAvatar: string = Environment.getDefaultProtagonistAvatar();
  public toolbarProtagonistAvatar: string;
  public protagonists: Protagonist[] = [];

  public location: Location;
  public locations: Location[] = [];
  public nextLocation: Location | null;
  public locationIndex: number;

  public fallback: boolean = false;

  private subscription: Subscription = new Subscription();

  public didLoad: boolean = false;
  public canGoBack: boolean = false;
  public canGoForward: boolean = false;
  public showTooltip: boolean = false;
  public isHidingTooltip: boolean = false;

  // TODO: Refactor into backend?
  private spaceStorytellerMapping = {
    'default': Environment.getStorytellerAvatarDefault(),
    '4872': Environment.getStorytellerAvatarRuhrort(), // Ruhrort DE
    '4968': Environment.getStorytellerAvatarRuhrort() // Ruhrort EN
  };

  constructor(
    private readonly navCtrl: NavController,
    private readonly menuCtrl: MenuController,
    private readonly popoverCtrl: PopoverController,
    private readonly storyService: StoryService,
    private readonly protagonistService: ProtagonistService,
    private readonly locationService: LocationService,
    private readonly routeService: RouteService,
    private readonly context: ContextService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly ngLocation : AngularLocation,
    private readonly settingsService: SettingsService,
    private readonly historyService: HistoryService
  ) { }

  ngOnInit(): void { }

  ionViewWillEnter(): void {
    this.resetPage();
  }

  async ionViewDidEnter(): Promise<void> {
    const slug: string = this.activatedRoute.snapshot.paramMap.get('slug');
    const quickOpen: string = this.activatedRoute.snapshot.paramMap.get('quickOpen');

    window.dispatchEvent(new CustomEvent('rdi::openStory', { detail: slug }));

    this.canGoBack = this.historyService.canGoBack();
    this.canGoForward = this.historyService.canGoForward();

    if (this.tooltip && this.tooltip.nativeElement) {
      this.tooltip.nativeElement.onclick = () => this.hideTooltip();
    }

    this.subscription.add(
      this.context.protagonistContextChange.subscribe((p: Protagonist) => {
        this.handleContextProtagonistChange(p);
      })
    );

    this.subscription.add(
      this.context.routeContextChange.subscribe((r: Route) => {
        this.contextRoute = r;
        this.updateToolbarProtagonist();
      })
    );

    this.story = await this.storyService.getBySlug(slug);
    this.protagonist = await this.protagonistService.getByID(this.story.protagonist);

    this.location = await this.locationService.getByID(this.story.location);
    this.context.setActiveProtagonist(this.protagonist);

    this.contextRoute = await this.autoUpdateContextRoute();

    this.locations = (await this.locationService.getByIDs(this.contextRoute.locations))
      .filter((location: Location) => location.type_slug === LocationType.STATION);

    const currentLocationIndex: number = this.locations.findIndex(({ id }) => id == this.location.id);

    this.nextLocation = this.locations[currentLocationIndex + 1];
    this.locationIndex = this.getLocationIndex();
    this.previousStory = await this.getPreviousStory();
    this.nextStory = await this.getNextStory();
    this.protagonists = await this.locationService.getProtagonists(this.location);
    this.showTooltip = this.shouldShowTooltip();

    DomHelper.whenReady(this.content.nativeElement).then(async () => {
      const ionContent: IonContent = this.content.nativeElement as IonContent;

      await ionContent.scrollToTop();

      if (quickOpen) {
        await this.doQuickOpen(quickOpen).catch(console.log);
      }

      this.didLoad = true;
    });
  }

  ionViewWillLeave(): void {
    this.closePopover();
    this.subscription.unsubscribe();
  }

  private resetPage(): void {
    this.subscription.unsubscribe();

    this.subscription = new Subscription();
    this.popover = undefined;
    this.popoverId = null;
    this.contextProtagonist = null;
    this.contextRoute = null;
    this.story = null;
    this.nextStory = null;
    this.previousStory = null;
    this.protagonist = null;
    this.toolbarProtagonistAvatar = null;
    this.protagonists = [];
    this.location = null;
    this.locations = [];
    this.nextLocation = null;
    this.locationIndex = null;
    this.fallback = null;
    this.didLoad = false;
    this.canGoBack = false;
    this.canGoForward = false;
    this.showTooltip = false;
    this.isHidingTooltip = false;
  }

  private async doQuickOpen(quickOpen: string): Promise<void> {
    switch (quickOpen) {
      case 'map': return await this.openMap();
      case 'storyteller': return await this.openStorytellerOverlay();
      case 'protagonists': return await this.openProtagonistList();
      case 'locations': return await this.openLocationList();
      default: break;
    }

    this.updateToolbarProtagonist();
  }

  public goBack(): void {
    this.historyService.goBack();
  }

  public goForward(): void {
    this.historyService.goForward();
  }

  private async autoUpdateContextRoute(): Promise<Route|null> {
    const routeId = Object.keys(this.location.indexByRoute).shift();
    const route: Route|null = await this.routeService.getByID(routeId);

    if (route) {
      this.contextRoute = route;
      this.context.setActiveRoute(route);
    }

    return route;
  }

  private updateToolbarProtagonist(): void {
    if (this.popoverId === 'storyteller') {
      this.toolbarProtagonistAvatar = (this.contextRoute && this.spaceStorytellerMapping[String(this.contextRoute.space)])
        ? this.spaceStorytellerMapping[String(this.contextRoute.space)]
        : this.spaceStorytellerMapping.default;
    } else if (this.contextProtagonist && this.contextProtagonist.avatar) {
      this.toolbarProtagonistAvatar = this.contextProtagonist.avatar.url;
    } else {
      this.toolbarProtagonistAvatar = this.defaultProtagonistAvatar;
    }
  }

  private updateUrl(): void {
    if (this.popoverId) {
      this.ngLocation.go(`/story/${this.story.slug}/${this.popoverId}`);
    } else {
      this.ngLocation.go(`/story/${this.story.slug}`);
    }
  }

  private async handleContextProtagonistChange(protagonist: Protagonist): Promise<void> {
    this.contextProtagonist = protagonist;

    this.updateToolbarProtagonist();

    if (this.popover) {
      let storyId: number;

      for (const sId of this.location.stories) {
        const story = await this.storyService.getByID(sId);
        if (story.protagonist === this.contextProtagonist.id) {
          storyId = story.id;

          break;
        }
      }

      this.closePopover();

      if (storyId) {
        const story: Story = await this.storyService.getByID(storyId);

        this.navCtrl.navigateRoot(`/story/${story.slug}`);
      }
    }
  }

  public async openProtagonistList(): Promise<void> {
    const key: string = 'protagonists';
    const match: boolean = (
      this.popover
      && this.popover.getAttribute('data-key') === key
    );

    this.showPin(key);

    if (this.popover) {
      this.hidePins();
      await this.closePopover();
    }

    if (!match) {
      await this.openPopover(key, ProtagonistsListComponent, {
        protagonists: this.protagonists,
      });

      this.hideTooltip();
    } else {
      this.updateUrl();
    }
  }

  public async openLocationList(): Promise<void> {
    const key: string = 'locations';
    const match: boolean = (
      this.popover
      && this.popover.getAttribute('data-key') === key
    );

    this.showPin(key);

    if (this.popover) {
      this.hidePins();
      await this.closePopover();
    }

    if (!match) {
      await this.openPopover(key, LocationListComponent, {
        locations: this.locations,
        currentLocation: this.location,
        onOpenLocation: (location: Location) => this.openLocation(location)
      });
    } else {
      this.updateUrl();
    }
  }

  private async openLocation(location: Location): Promise<void> {
    if (location.stories) {
      const storyId: number = location.stories[0];
      const story: Story = await this.storyService.getByID(storyId);

      if (story) {
        this.navCtrl.navigateRoot(`/story/${story.slug}/map`);
      }
    }
  }

  public async openMap(force: boolean = false): Promise<void> {
    const key: string = 'map';

    this.showPin(key);

    const match: boolean = (
      this.popover
      && this.popover.getAttribute('data-key') === key
    );

    if (this.popover) {
      this.hidePins();
      await this.closePopover();
    }

    if (!match && (!this.fallback || force)) {
      await this.openPopover(key, MapComponent, {
        currentLocation: this.location,
        currentStory: this.story,
        onShouldDismiss: async () => await this.closePopover(),
        onShowMore: async (fallbackLocation: Location, location: Location) => {
          this.fallback = true;

          const story: Story|null = location.stories
            ? await this.storyService.getByID(location.stories[0])
            : null;

          const previousState = {
            protagonist: this.protagonist,
            location: this.location,
            story: this.story,
            contextRoute: this.contextRoute,
            locationIndex: this.locationIndex,
          };

          if (story) {
            this.protagonist = await this.protagonistService.getByID(story.protagonist);
            this.location = await this.locationService.getByID(story.location);
            this.story = story;
            this.contextRoute = await this.autoUpdateContextRoute();
            this.locationIndex = this.getLocationIndex();

            this.updateUrl();
            // this.ngLocation.go(`/story/${story.slug}`);
          }

          this.openStorytellerOverlay(async () => {
            if (fallbackLocation) {
              this.protagonist = previousState.protagonist;
              this.location = previousState.location;
              this.story = previousState.story;
              this.contextRoute = await this.autoUpdateContextRoute();
              this.locationIndex = previousState.locationIndex;

              await this.openMap(true);

              this.fallback = false;
            }
          });
        },
      });
    } else {
      this.updateUrl();
    }
  }

  private hidePins(): void {
    this.popoverId = null;

    jQuery(this.header.nativeElement)
      .find('.popover-arrow')
      .removeClass('active');
  }

  private showPin(pinId: string): void {
    this.hidePins();

    this.popoverId = pinId;

    jQuery(this.header.nativeElement)
      .find(`.popover-arrow[data-pin-id="${pinId}"]`)
      .addClass('active');
  }

  public async openStorytellerOverlay(handleDismiss?: Function): Promise<void> {
    const key: string = 'storyteller';
    const match: boolean = (
      this.popover
      && this.popover.getAttribute('data-key') === key
    );

    this.showPin(key);

    if (this.popover) {
      this.hidePins();
      await this.closePopover();
    }

    if (!match) {
      await this.openPopover(
        key,
        StorytellerOverlayComponent,
        {
          location: this.location,
          onProceed: () => {
            this.closePopover(true);
            this.context.setActiveProtagonist(this.protagonist);
          }
        },
        handleDismiss
      );

      this.updateToolbarProtagonist();

      if (this.popover) {
        this.popover.onWillDismiss().then(() => this.updateToolbarProtagonist());
      }
    } else {
      this.updateUrl();
    }
  }

  public async closePopover(forceClose: boolean = false): Promise<void> {
    if (this.popover) {
      await this.popover.dismiss({ forceClose });

      this.popover = undefined;

      this.hidePins();

      jQuery('ion-popover').remove();

      this.updateToolbarProtagonist();
    }
  }

  private async openPopover(
    key: string,
    component: any,
    props: any = {},
    handleDismiss?: Function,
  ): Promise<void> {
    this.showPin(key);

    await this.closePopover();

    const popover = await this.popoverCtrl.create({
      component: component,
      animated: false,
      showBackdrop: false,
      backdropDismiss: false,
      mode: 'ios',
      cssClass: 'popover-fullscreen',
      componentProps: props,
    });

    this.menuCtrl.enable(false);

    popover.onWillDismiss().then(() => this.menuCtrl.enable(true));

    if (handleDismiss) {
      popover.onDidDismiss().then((details) => {
        const { data } = details;

        if (data && !data.forceClose) {
          handleDismiss();
        }
      });
    }

    await popover.present();

    popover.setAttribute('data-key', key);

    this.popover = popover;

    this.updateUrl();
  }

  private getNextStory(): Promise<Story | null> {
    if (!this.contextRoute) {
      return null;
    }

    const index: number = this.getLocationIndex();

    if (index + 1 >= this.locations.length) {
      return null;
    }

    return this.getStoryWithSameProtagonist(
      this.locations[index + 1],
      this.protagonist
    );
  }

  private getPreviousStory(): Promise<Story> {
    if (!this.contextRoute) {
      return null;
    }

    const index: number = this.getLocationIndex();

    if (index <= 0) {
      return null;
    }

    return this.getStoryWithSameProtagonist(
      this.locations[index - 1],
      this.protagonist,
    );
  }

  public async gotoNextStory(): Promise<void> {
    const story: Story = await this.getNextStory();

    if (story) {
      await this.navCtrl.navigateForward(`/story/${story.slug}/map`);
    } else {
      throw new Error('No story available');
    }
  }

  public async gotoPreviousStory(): Promise<void> {
    const story: Story = await this.getPreviousStory();

    if (story) {
      await this.navCtrl.navigateBack(`/story/${story.slug}/map`);
    } else {
      throw new Error('No story available');
    }
  }

  private async getStoryWithSameProtagonist(
    location: Location,
    protagonist: Protagonist,
  ): Promise<Story | undefined> {
    if (location && location.stories && protagonist) {
      const story: Story = (await this.storyService.getAll())
        .filter((s: Story) => location.stories.includes(s.id))
        .find((s: Story) => s.protagonist === protagonist.id);

      if (story) {
        return story;
      } else if (location.stories[0]) {
        return await this.storyService.getByID(location.stories[0]);
      } else {
        console.warn('Missing location and/or location\'s stories', { location });
      }
    }
  }

  private getLocationIndex(): number {
    if (
      this.location !== undefined &&
      this.location.indexByRoute !== undefined &&
      this.location.indexByRoute[this.contextRoute.id] !== undefined
    ) {
      return this.location.indexByRoute[this.contextRoute.id];
    }

    return -1;
  }

  handleScroll = (e: any) => {
    if (this.showTooltip && e.detail.currentY > 250) {
      this.hideTooltip();
    }
  };

  private shouldShowTooltip(): boolean {
    return (
      !this.settingsService.getTooltipShown()
      && this.protagonists
      && this.protagonists.length > 1
    );
  }

  private hideTooltip(): void {
    if (this.showTooltip && !this.isHidingTooltip) {
      this.isHidingTooltip = true;

      jQuery.when(jQuery(this.tooltip.nativeElement).fadeOut()).done(() => {
        this.settingsService.setTooltipShown();

        this.showTooltip = false;
        this.isHidingTooltip = false;
      });
    }
  }
}
